import React, { createContext, useContext, useState, useEffect } from "react";
import { client } from "../lib/sanity";
import { allPostsQuery } from "./queries";
import Fuse from "fuse.js";

interface Post {
  _id: string;
  title: string;
  slug: { current: string };
  excerpt?: string;
  publishedAt: string;
  featured: boolean;
  claps: number;
}

interface SearchContextType {
  searchPosts: (query: string) => Post[];
  isLoading: boolean;
  error: Error | null;
}

// Create the context
const SearchContext = createContext<SearchContextType | undefined>(undefined);

// Create and export the hook
export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
};

// Export the provider
export const SearchProvider = ({ children }: { children: React.ReactNode }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [fuse, setFuse] = useState<Fuse<Post> | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const fetchedPosts = await client.fetch<Post[]>(allPostsQuery);
        setPosts(fetchedPosts);
        const fuseInstance = new Fuse<Post>(fetchedPosts, {
          keys: [
            { name: "title", weight: 0.4 },
            { name: "excerpt", weight: 0.3 },
            { name: "bodyText", weight: 0.2 },
            { name: "categories.title", weight: 0.1 },
          ],
          threshold: 0.3,
          distance: 100,
          includeScore: true,
        });
        setFuse(fuseInstance);
        setIsLoading(false);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to fetch posts")
        );
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const searchPosts = (query: string): Post[] => {
    if (!fuse || query.length < 2) return [];
    return fuse.search(query).map((result) => result.item);
  };

  return (
    <SearchContext.Provider value={{ searchPosts, isLoading, error }}>
      {children}
    </SearchContext.Provider>
  );
};
